import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import {
  api,
  bidDisplayLabel,
  type BidWorkflow,
  type WorkflowType,
  type WorkflowMeta,
  type WorkflowNode,
} from '../lib/api'
import { deriveProgress } from '../lib/workflow'
import { WorkflowDag } from './WorkflowDag'
import { NodeDrawer } from './NodeDrawer'

interface DashboardViewProps {
  bid: string | null
  onOpenRoom: (room: string) => void
}

type ViewMode = 'dag' | 'table'

const STATUS_GLOBAL_LABELS: Record<BidWorkflow['statusGlobal'], string> = {
  a_qualifier: 'À qualifier',
  en_cours: 'En cours',
  soumis: 'Soumis',
  gagne: 'Gagné',
  perdu: 'Perdu',
}

const STATUS_GLOBAL_CLASS: Record<BidWorkflow['statusGlobal'], string> = {
  a_qualifier: 'node-a-faire',
  en_cours: 'node-en-cours',
  soumis: 'node-valide',
  gagne: 'node-valide',
  perdu: 'node-bloque',
}

const STATUT_LABEL: Record<WorkflowNode['statut'], string> = {
  a_faire: 'À faire',
  en_cours: 'En cours',
  a_revoir: 'À revoir',
  valide: 'Validé',
  bloque: 'Bloqué',
}

function daysFromNow(iso: string | undefined): number | null {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return Math.round((d.getTime() - Date.now()) / 86_400_000)
}

export function DashboardView({ bid, onOpenRoom }: DashboardViewProps) {
  const { t } = useLang()
  const qc = useQueryClient()
  const [viewMode, setViewMode] = useState<ViewMode>('dag')
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [workflowType] = useState<WorkflowType>('ao-standard')

  const bidsQuery = useQuery({ queryKey: ['bids'], queryFn: api.bids })
  const bidRecord = bidsQuery.data?.bids.find((b) => b.id === bid)
  const bidName = bidRecord?.name ?? bid ?? ''
  // The header's fallback names the bid the way the breadcrumb does ("Client - Bid name"). The
  // workflow's own meta.titre / meta.client are a separate concept, and stay as they are.
  const bidLabel = bidRecord ? bidDisplayLabel(bidRecord) : (bid ?? '')

  const wfQuery = useQuery({
    queryKey: ['workflow', bid],
    queryFn: () => api.workflow(bid!),
    enabled: Boolean(bid),
    retry: false,
  })

  const workflow = wfQuery.data
  const hasWorkflow = Boolean(workflow && workflow.nodes.length > 0)
  const selectedNode = workflow?.nodes.find((n) => n.nodeId === selectedNodeId) ?? null

  async function createWorkflow(): Promise<void> {
    if (!bid || creating) return
    setCreating(true)
    try {
      const meta: WorkflowMeta = { titre: bidName, client: bidName }
      await api.createWorkflow(bid, workflowType, meta)
      await qc.invalidateQueries({ queryKey: ['workflow', bid] })
      await qc.invalidateQueries({ queryKey: ['working-docs', bid] })
      await qc.invalidateQueries({ queryKey: ['docs', bid] })
    } finally {
      setCreating(false)
    }
  }

  function openRoom(room: string): void {
    setSelectedNodeId(null)
    onOpenRoom(room)
  }

  if (!bid) {
    return <div className="dashboard-empty">Select a bid to see its workflow.</div>
  }

  if (wfQuery.isLoading) {
    return <div className="dashboard-empty">Loading…</div>
  }

  if (!hasWorkflow) {
    return (
      <div className="dashboard-empty">
        <p>{t('wf_no_workflow')}</p>
        <button className="btn primary" disabled={creating} onClick={() => void createWorkflow()}>
          {creating ? '…' : t('wf_create')}
        </button>
      </div>
    )
  }

  const progress = deriveProgress(workflow!.nodes)
  const daysLeft = daysFromNow(workflow!.meta.date_limite_depot)

  return (
    <div className="dashboard">
      {/* ── Header band ─────────────────────────────────────────────────────── */}
      <div className="dash-header">
        <div className="dash-header-left">
          <h1 className="dash-titre">{workflow!.meta.titre || bidLabel}</h1>
          <span className="dash-client">{workflow!.meta.client}</span>
        </div>
        <div className="dash-header-right">
          <span className={`wf-pill ${STATUS_GLOBAL_CLASS[workflow!.statusGlobal]}`}>
            {STATUS_GLOBAL_LABELS[workflow!.statusGlobal]}
          </span>
          {daysLeft !== null && (
            <span
              className={`dash-deadline${daysLeft < 0 ? ' overdue' : daysLeft < 7 ? ' urgent' : ''}`}
              title={workflow!.meta.date_limite_depot}
            >
              {daysLeft >= 0
                ? `${daysLeft} ${t('wf_days_left')}`
                : `${-daysLeft} ${t('wf_days_overdue')}`}
            </span>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="dash-meta-row">
        {workflow!.meta.date_reception && (
          <span className="dash-meta-item">
            <span className="dash-meta-label">{t('wf_date_reception')}</span>
            {workflow!.meta.date_reception}
          </span>
        )}
        {workflow!.meta.date_limite_depot && (
          <span className="dash-meta-item">
            <span className="dash-meta-label">{t('wf_date_deadline')}</span>
            {workflow!.meta.date_limite_depot}
          </span>
        )}
        {workflow!.meta.chef_de_projet_offre && (
          <span className="dash-meta-item">
            <span className="dash-meta-label">{t('wf_chef_projet')}</span>
            {workflow!.meta.chef_de_projet_offre}
          </span>
        )}
        {workflow!.meta.valeur_estimee_eur != null && workflow!.meta.valeur_estimee_eur > 0 && (
          <span className="dash-meta-item">
            <span className="dash-meta-label">{t('wf_valeur')}</span>
            {workflow!.meta.valeur_estimee_eur.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
          </span>
        )}
      </div>

      {/* ── Progress summary ─────────────────────────────────────────────────── */}
      <div className="dash-progress">
        <div className="dash-progress-bar-wrap">
          <div
            className="dash-progress-bar"
            style={{ width: `${progress.pct}%` }}
            role="progressbar"
            aria-valuenow={progress.pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${progress.pct}% complete`}
          />
        </div>
        <span className="dash-progress-label">
          {progress.valide}/{progress.total} {t('wf_nodes_done')} · {progress.pct}%
        </span>
        <span
          className={`dash-critical-badge ${progress.criticalPathBlocked ? 'critical-blocked' : 'critical-ok'}`}
        >
          {progress.criticalPathBlocked ? '⚠ ' + t('wf_critical_blocked') : '✓ ' + t('wf_critical_ok')}
        </span>
      </div>

      {/* ── View toggle ──────────────────────────────────────────────────────── */}
      <div className="dash-view-toggle">
        <button
          className={`mini${viewMode === 'dag' ? ' ok' : ''}`}
          aria-pressed={viewMode === 'dag'}
          onClick={() => setViewMode('dag')}
        >
          {t('wf_dag_view')}
        </button>
        <button
          className={`mini${viewMode === 'table' ? ' ok' : ''}`}
          aria-pressed={viewMode === 'table'}
          onClick={() => setViewMode('table')}
        >
          {t('wf_table_view')}
        </button>
      </div>

      {/* ── DAG / Table ──────────────────────────────────────────────────────── */}
      <div className="dash-content" onClick={(e) => { if (e.currentTarget === e.target) setSelectedNodeId(null) }}>
        {viewMode === 'dag' ? (
          <WorkflowDag
            nodes={workflow!.nodes}
            onSelectNode={setSelectedNodeId}
            selectedNodeId={selectedNodeId}
          />
        ) : (
          <WorkflowTable
            nodes={workflow!.nodes}
            onSelectNode={setSelectedNodeId}
            selectedNodeId={selectedNodeId}
          />
        )}
      </div>

      {/* ── Node drawer (slide-in detail) ─────────────────────────────────── */}
      {selectedNode && workflow && (
        <NodeDrawer
          node={selectedNode}
          workflow={workflow}
          onClose={() => setSelectedNodeId(null)}
          onOpenRoom={openRoom}
        />
      )}
    </div>
  )
}

// ─── Table view ──────────────────────────────────────────────────────────────

function WorkflowTable({
  nodes,
  onSelectNode,
  selectedNodeId,
}: {
  nodes: WorkflowNode[]
  onSelectNode: (id: string) => void
  selectedNodeId: string | null
}) {
  return (
    <table className="htable wf-table" aria-label="Workflow nodes">
      <thead>
        <tr>
          <th className="hcol-wide">Section</th>
          <th className="hcol-sm">Statut</th>
          <th className="hcol-sm">Critique</th>
          <th className="hcol-md">Responsable</th>
          <th className="hcol-sm">Rooms</th>
        </tr>
      </thead>
      <tbody>
        {nodes.map((n) => (
          <tr
            key={n.nodeId}
            className={`wf-row${selectedNodeId === n.nodeId ? ' wf-row-sel' : ''}${!n.enabled ? ' wf-row-disabled' : ''}`}
            onClick={() => onSelectNode(n.nodeId)}
            style={{ cursor: 'pointer' }}
          >
            <td>
              <span className="wf-section-label">{n.section}</span>
            </td>
            <td>
              <span className={`wf-pill node-${n.statut.replace('_', '-')}`}>
                {STATUT_LABEL[n.statut]}
              </span>
            </td>
            <td style={{ textAlign: 'center' }}>
              {n.surCheminCritique ? <span title="Critical path" aria-label="Critical path">⚑</span> : '—'}
            </td>
            <td style={{ color: 'var(--muted)', fontSize: 13 }}>{n.owner ?? '—'}</td>
            <td>
              {n.artifactRoom ? (
                <span className="mono" style={{ fontSize: 11 }}>✓</span>
              ) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
